import { useMemo } from "react";
import { PowerSyncDatabase, Table, column } from "@powersync/web";
import { useQuery, useStatus } from "@powersync/react";
import { TABLES, fromRow, psSchema, type RowOf, type TableName } from "@cliqsuite/db/powersync";

// Everything the store and business buckets give a paired till (powersync/sync-rules.yaml).
const SYNCED: TableName[] = [
  "stores", "businesses", "devices", "staff", "staff_sessions", "staff_shifts", "roles", "store_payment_methods",
  "categories", "products", "product_variant_groups", "product_variant_options", "product_variants", "add_ons",
  "store_inventory", "inventory_adjustments", "ingredients", "product_ingredients",
  "orders", "order_items", "order_item_option_selections", "kitchen_tickets", "drawer_closes",
  "tables", "table_sessions", "reservations",
  "members", "member_store_points", "point_transactions", "loyalty_configs", "business_loyalty_settings", "rewards", "vouchers", "promos",
  "printers", "print_jobs", "print_hubs", "payment_method_configs",
];

const schema = psSchema(SYNCED, {
  // Offline checkout queue (lib/outbox.ts). Local-only: never synced, and never cleared by a re-sync.
  pending_sales: new Table(
    {
      request_body: column.text,
      queued_at: column.text,
      updated_at: column.text,
      attempts: column.integer,
      last_error: column.text,
      needs_attention: column.integer,
    },
    { localOnly: true },
  ),
});

// A till is one tab on a tablet/kiosk; some kiosk browsers block SharedWorker, so sync runs in this tab.
export const db = new PowerSyncDatabase({ schema, database: { dbFilename: "pos.db", enableMultiTabs: false } });

/**
 * A store's kitchen tickets the till shows: unfinished ones and the last day's. Every synced row
 * re-runs these queries, so a store's whole history must not go through them. Timestamps here come
 * as Postgres text or as toISOString(), so only their first 19 characters are comparable.
 */
export const RECENT_TICKETS = `SELECT * FROM kitchen_tickets WHERE store_id = $1 AND (status <> 'done' OR replace(substr(updated_at, 1, 19), 'T', ' ') >= datetime('now', '-1 day'))`;

/** Same for orders: anything still open or unpaid, and the last day's (today's cash total needs them). */
export const RECENT_ORDERS = `SELECT * FROM orders WHERE store_id = $1 AND (status = 'open' OR payment_status = 'pending' OR replace(substr(created_at, 1, 19), 'T', ' ') >= datetime('now', '-1 day'))`;

/**
 * Live rows of one local table, parsed back to their zod types (booleans, arrays, numbers).
 * Same shape the PGlite hook had: `undefined` until the first result, then `{ rows }`.
 * `$1`-style placeholders work as-is in SQLite; `true`/`false` too.
 */
export function useRows<N extends TableName>(table: N, sql: string, params: unknown[] = []): { rows: RowOf<N>[] } | undefined {
  const { data, isLoading } = useQuery<Record<string, unknown>>(sql, params);
  return useMemo(
    () => (isLoading ? undefined : { rows: data.map((r) => fromRow(TABLES[table][0], r)) as RowOf<N>[] }),
    [data, isLoading, table],
  );
}

/** TanStack-style: `{ data, isReady }`, where isReady also waits for this till's first full sync
 * (anything that WRITES based on "row missing" must wait for that). */
export function useLive<N extends TableName>(table: N, sql: string, params: unknown[] = []) {
  const result = useRows(table, sql, params);
  const synced = useStatus().hasSynced ?? false;
  return { data: result?.rows ?? [], isReady: !!result && synced };
}

/** Raw rows (aggregates, local-only tables) — no parsing. */
export function useSql<T>(sql: string, params: unknown[] = []): { rows: T[] } | undefined {
  const { data, isLoading } = useQuery<T>(sql, params);
  return useMemo(() => (isLoading ? undefined : { rows: data }), [data, isLoading]);
}

/** One-off parsed read, for event handlers. */
export async function getRows<N extends TableName>(table: N, sql: string, params: unknown[] = []): Promise<RowOf<N>[]> {
  const rows = await db.getAll<Record<string, unknown>>(sql, params);
  return rows.map((r) => fromRow(TABLES[table][0], r)) as RowOf<N>[];
}

/** Local write of a full new row; PowerSync uploads it to the table's sync-api route. */
export async function insertRow<N extends TableName>(table: N, row: Partial<RowOf<N>> & { id: string }): Promise<void> {
  const cols = Object.keys(row).filter((c) => c !== "id");
  await db.execute(`INSERT INTO ${table} (id, ${cols.join(", ")}) VALUES (?, ${cols.map(() => "?").join(", ")})`, [
    row.id,
    ...cols.map((c) => toSqlite((row as Record<string, unknown>)[c])),
  ]);
}

/** Local write of some columns; uploaded as a PATCH. */
export async function updateRow<N extends TableName>(table: N, id: string, changes: Partial<RowOf<N>>): Promise<void> {
  const cols = Object.keys(changes);
  if (!cols.length) return;
  await db.execute(`UPDATE ${table} SET ${cols.map((c) => `${c} = ?`).join(", ")} WHERE id = ?`, [
    ...cols.map((c) => toSqlite((changes as Record<string, unknown>)[c])),
    id,
  ]);
}

function toSqlite(v: unknown): unknown {
  if (v === undefined) return null;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (v !== null && typeof v === "object") return JSON.stringify(v);
  return v;
}
